import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "./DeviceSales.scss";
import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import {
  formatDateTimeTrInAppTz,
  formatInstantDateShortTr,
} from "../../../utils/appTime";
import { generateInvoicePDF } from "../../../utils/generateOfferPDF";
import PdfPreviewModal from "../../../components/CustomUI/PdfPreviewModal";
import { usePdfPreview } from "../../../hooks/usePdfPreview";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import {
  buildAdaptiveSingleFilterSection,
  buildRadioFilterSection,
} from "../shared/buildListFilterSections";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import {
  Modal,
  ModalHeader,
  ModalBody,
} from "../../../components/BaseUI/Modal";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import { useTitle } from "../../../hooks/useTitle";
// Shared price display: follows the system USD/TL toggle in the header
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";

import {
  FiShoppingCart,
  FiCalendar,
  FiTrash2,
  FiServer,
  FiSave,
  FiDollarSign,
  FiSearch,
  FiCreditCard,
  FiPackage,
  FiTruck,
  FiFileText,
  FiEdit2,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiTool,
  FiCheckSquare,
  FiAlertCircle,
  FiInfo,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const DeviceSales = () => {
  useTitle("Rovion MES - Siparislari");
  const fx = useExchangeRateOptional();
  const [sales, setSales] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sidebar and modal states
  const [editSidebarOpened, setEditSidebarOpened] = useState(false);
  const [detailSidebarOpened, setDetailSidebarOpened] = useState(false);
  const [removeDeviceModalOpen, setRemoveDeviceModalOpen] = useState(false);
  const [modelDetailsModalOpen, setModelDetailsModalOpen] = useState(false);
  const [selectedSaleForModelDetails, setSelectedSaleForModelDetails] =
    useState(null);

  // Sale info state
  const [saleInfo, setSaleInfo] = useState({
    saleName: "",
    companyId: "",
    devices: [],
    paymentStatus: "pending",
    paymentMethod: "cash",
  });

  // Filter states (null = all, matches Products page pattern)
  const [filterCompany, setFilterCompany] = useState(null);
  const [filterDeliveryStatus, setFilterDeliveryStatus] = useState(null);
  const [filterPaymentStatus, setFilterPaymentStatus] = useState(null);

  const parseRateNum = (val) => {
    if (val === undefined || val === null || val === "") return null;
    const num = Number(String(val).trim().replace(",", "."));
    return !isNaN(num) && num > 0 ? num : null;
  };

  // Device management states
  const [devices, setDevices] = useState([]);
  const [pricePerDevice, setPricePerDevice] = useState(0);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState("");
  const [currentDevicePage, setCurrentDevicePage] = useState(1);
  const [devicesPerPage] = useState(5);
  const [modelInfoMap, setModelInfoMap] = useState({});

  // Transfer to Production states
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const {
    pdfPreview,
    closePdfPreview,
    openPdfPreview,
    downloadPdfFromPreview,
  } = usePdfPreview();
  const [selectedSaleForTransfer, setSelectedSaleForTransfer] = useState(null);
  const [transferData, setTransferData] = useState(null);
  const [loadingTransferData, setLoadingTransferData] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // Removal modal states
  const [deviceToRemove, setDeviceToRemove] = useState(null);
  const [removalType, setRemovalType] = useState("iade");
  const [removalDescription, setRemovalDescription] = useState("");
  const [replacementDevice, setReplacementDevice] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);

  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken, user } = useAuth();
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/device-sales",
      user.pages || [],
      !!user.fullAccess,
    );

  // Translations object
  const translations = {
    SUCCESS: translate("UI.SUCCESS"),
    ERROR: translate("UI.ERROR"),
    CANCEL_SALE_INSTEAD: translate("UI.CANCEL_SALE_INSTEAD"),
    COMPLETED_SALE_CANCEL_CONFIRM: translate(
      "UI.COMPLETED_SALE_CANCEL_CONFIRM",
    ),
    CANCEL_SALE: translate("UI.CANCEL_SALE"),
    SALE_CANCEL_ERROR: translate("UI.SALE_CANCEL_ERROR"),
    SALE_DELETE_ERROR: translate("UI.SALE_DELETE_ERROR"),
  };

  const saleDeliveredTotal = (sale) =>
    sale?.models?.reduce((sum, m) => sum + (m.deliveredCount || 0), 0) || 0;

  const saleDateSortMs = (sale) => {
    if (!sale?.saleDate) return 0;
    const d = new Date(sale.saleDate);
    if (!Number.isFinite(d.getTime())) return 0;
    if (sale.saleTime && typeof sale.saleTime === "string") {
      const parts = sale.saleTime.trim().split(":");
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      if (Number.isFinite(h) && Number.isFinite(m)) {
        d.setHours(h, m, 0, 0);
      }
    }
    return d.getTime();
  };

  const headers = [
    {
      name: translate("UI.SALE_NAME") || "Sale Name",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.saleName || "").localeCompare(
          String(b.saleName || ""),
          undefined,
          {
            numeric: true,
            sensitivity: "base",
          },
        ),
    },
    {
      name: translate("UI.COMPANY") || "Company",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.companyId?.name || "").localeCompare(
          String(b.companyId?.name || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: translate("UI.TOTAL_AMOUNT") || "Total Amount",
      dataType: "number",
      id: 2,
      customSorter: (a, b) =>
        (Number(a.totalAmount) || 0) - (Number(b.totalAmount) || 0),
    },
    {
      name: translate("UI.EXCHANGE_RATE") || "Döviz Kuru",
      dataType: "number",
      id: 9,
      customSorter: (a, b) => {
        const rateA =
          parseRateNum(a.exchangeRate) ?? parseRateNum(fx?.rate) ?? 0;
        const rateB =
          parseRateNum(b.exchangeRate) ?? parseRateNum(fx?.rate) ?? 0;
        return rateA - rateB;
      },
    },
    {
      name: translate("UI.TOTAL_DEVICES") || "Total Devices",
      dataType: "number",
      id: 3,
      customSorter: (a, b) =>
        (Number(a.totalDevices) || 0) - (Number(b.totalDevices) || 0),
    },
    {
      name: translate("UI.TOTAL_DELIVERED") || "Total Delivered",
      dataType: "number",
      id: 4,
      customSorter: (a, b) => saleDeliveredTotal(a) - saleDeliveredTotal(b),
    },
    {
      name: translate("UI.SALE_DATE") || "Sale Date",
      dataType: "date",
      id: 5,
      customSorter: (a, b) => saleDateSortMs(a) - saleDateSortMs(b),
    },
    {
      name: translate("UI.DELIVERY_STATUS") || "Delivery Status",
      dataType: "text",
      id: 6,
      customSorter: (a, b) =>
        String(a.status || "").localeCompare(String(b.status || "")),
    },
    {
      name: translate("UI.PAYMENT_STATUS") || "Payment Status",
      dataType: "text",
      id: 7,
      customSorter: (a, b) =>
        String(a.paymentStatus || "").localeCompare(
          String(b.paymentStatus || ""),
        ),
    },
    { name: translate("UI.ACTIONS"), id: 8, sortable: false },
  ];

  // Use column visibility hook
  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("product-sales-list", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.PRODUCT_SALES") },
  ];

  useEffect(() => {
    if (user) {
      fetchSales();
      fetchAvailableDevices();
    }
  }, [user]);

  // Fetch sales data from backend
  const fetchSales = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/device-sales`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (response.data.success && response.data.sales) {
        setSales(response.data.sales);
      } else {
        setSales([]);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
      setToasts([
        {
          id: Date.now(),
          title: translate("UI.ERROR"),
          description:
            error.response?.data?.message ||
            "Siparislar y?klenirken bir hata olustu",
          appearance: "danger",
        },
      ]);
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available devices
  const fetchAvailableDevices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/devices`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const devicesData = response.data.devices || response.data || [];
      setDevices(devicesData.filter((device) => device.status === "assembly"));
    } catch (error) {
      console.error("Error fetching available devices:", error);
    }
  };

  const getSaleStatusAppearance = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "partial":
        return "warning";
      case "pending":
        return "danger";
      default:
        return "neutral";
    }
  };

  const getDeliveryStatusAppearance = (status) => {
    switch (status) {
      case "completed":
        return "success-subtle";
      case "partial_delivery":
        return "warning-subtle";
      case "pending":
        return "neutral-subtle";
      case "cancelled":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const getDeliveryStatusLabel = (status) => {
    const labels = {
      pending: translate("UI.PENDING") || "Pending",
      partial_delivery: translate("UI.PARTIAL_DELIVERY") || "Partial Delivery",
      completed: translate("UI.COMPLETED") || "Completed",
      cancelled: translate("UI.CANCELLED") || "Cancelled",
    };
    return labels[status] || status;
  };

  const getApprovalStatusAppearance = (status) => {
    switch (status) {
      case "pending":
        return "warning-subtle";
      case "approved":
        return "success-subtle";
      case "rejected":
        return "danger-subtle";
      case "not_required":
        return "default";
      default:
        return "default";
    }
  };

  const getApprovalStatusLabel = (status) => {
    const labels = {
      pending: "Onay Bekliyor",
      approved: "Onaylandi",
      rejected: "Reddedildi",
      not_required: "Onay Yok",
    };
    return labels[status] || null;
  };

  const isSaleApprovalPending = (sale) =>
    sale?.approvalStatus === "pending" ||
    sale?.deliveryApproval?.allowed === false;

  const handleEditSale = (sale) => {
    if (sale?.approvalStatus === "pending") {
      setToasts([
        {
          id: Date.now(),
          title: "D?zenleme Engellendi",
          description: "Onay bekleyen siparis d?zenlenemez.",
          appearance: "warning",
        },
      ]);
      return;
    }
    navigate(`/device-sales/edit/${sale._id}`);
  };

  const handleDeleteSale = (sale) => {
    confirm({
      title: `${translate("UI.DELETE_SALE") || "Delete Sale"}?`,
      message:
        translate("UI.DELETE_SALE_CONFIRM") ||
        "Are you sure you want to delete this sale?",
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE") || "Delete",
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await axios.delete(
            `${API_URL}/api/device-sales/${sale._id}`,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            },
          );

          if (response.data.success) {
            setToasts([
              {
                description:
                  response.data.message ||
                  translate("UI.SALE_DELETED_SUCCESSFULLY") ||
                  "Sale deleted successfully",
                id: Date.now(),
                title: translate("UI.SUCCESS") || "Success",
                appearance: "success",
              },
            ]);
            await fetchSales();
          } else {
            throw new Error(response.data.message || "Failed to delete sale");
          }
        } catch (error) {
          console.error("Delete sale error:", error);
          setToasts([
            {
              description:
                error.response?.data?.message ||
                error.message ||
                translate("UI.SALE_DELETE_ERROR") ||
                "Error deleting sale",
              id: Date.now(),
              title: translate("UI.ERROR") || "Error",
              appearance: "danger",
            },
          ]);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleCreateDelivery = (sale) => {
    if (sale?.deliveryApproval?.allowed === false) {
      setToasts([
        {
          id: Date.now(),
          title: "Sevkiyat Onayi Bekleniyor",
          description:
            sale.deliveryApproval.reason ||
            "Bu siparis i?in sevkiyat onayi tamamlanmalidir.",
          appearance: "warning",
        },
      ]);
      return;
    }
    navigate(`/deliveries/create?saleId=${sale._id}`);
  };

  const limitCompanyName = (name, maxWords = 3) => {
    if (!name) return "-";
    const words = name.trim().split(/\s+/);
    if (words.length <= maxWords) return name;
    return words.slice(0, maxWords).join(" ") + "...";
  };

  const buildInvoicePayload = (sale) => {
    const company = sale.companyId || {};
    return {
      saleId: sale._id,
      invoiceNumber: `INV-${sale._id.slice(-8).toUpperCase()}-${Date.now()
        .toString()
        .slice(-6)}`,
      companyId: company._id || sale.companyId || null,
      companyName: company.name || null,
      companyCode: company.code || null,
      companyAddress: company.address || "",
      companyEmail: company.email || "",
      taxOffice: company.taxOffice || "",
      taxNumber: company.taxNumber || "",
      saleName: sale.saleName || "",
      totalAmount: sale.totalAmount || 0,
      totalDevices: sale.totalDevices || 0,
      items:
        sale.models?.map((model) => ({
          modelId: model.modelId?._id || model.modelId,
          modelName: model.modelId?.name || "Unknown Model",
          modelCode: model.modelId?.code || "",
          quantity: model.count || 0,
          unitPrice: model.modelId?.price || 0,
          totalPrice: (model.modelId?.price || 0) * (model.count || 0),
        })) || [],
      paymentMethod: sale.paymentMethod || "cash",
      paymentStatus: sale.paymentStatus || "pending",
      invoiceDate: new Date().toISOString(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      notes: sale.notes || "",
      status: "draft",
      createdByName: sale.createdBy?.name || "",
    };
  };

  const previewInvoicePdf = (invoiceData, sale) => {
    openPdfPreview({
      title: `Fatura PDF – ${sale?.saleName || invoiceData?.saleName || "Fatura"}`,
      generate: (currencyOptions) =>
        generateInvoicePDF(invoiceData, sale, {
          action: "blob",
          ...currencyOptions,
          exchangeRate:
            parseRateNum(sale?.exchangeRate) ?? currencyOptions?.exchangeRate,
        }),
    });
  };

  const handleCreateInvoice = async (sale) => {
    if (isSaleApprovalPending(sale)) {
      setToasts([
        {
          id: Date.now(),
          title: "Onay Bekleniyor",
          description:
            sale.deliveryApproval?.reason ||
            "Onay tamamlanmadan fatura olusturulamaz.",
          appearance: "warning",
        },
      ]);
      return;
    }
    try {
      setLoading(true);
      const invoiceData = buildInvoicePayload(sale);

      const response = await axios.post(
        `${API_URL}/api/invoices`,
        invoiceData,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      if (response.data.success) {
        const invoiceDataForPDF = {
          ...invoiceData,
          ...(response.data.invoice || {}),
        };
        previewInvoicePdf(invoiceDataForPDF, sale);
        setToasts([
          {
            description:
              response.data.message ||
              translate("UI.INVOICE_CREATED_SUCCESSFULLY") ||
              "Fatura olusturuldu",
            id: Date.now(),
            title: translate("UI.SUCCESS") || "Success",
            appearance: "success",
          },
        ]);
      } else {
        throw new Error(response.data.message || "Failed to create invoice");
      }
    } catch (error) {
      console.error("Error creating invoice:", error.response?.data || error);
      if (error.response?.status === 404 || error.response?.status === 501) {
        const invoiceData = buildInvoicePayload(sale);
        localStorage.setItem(
          `invoice_${sale._id}`,
          JSON.stringify(invoiceData),
        );
        previewInvoicePdf(invoiceData, sale);
        setToasts([
          {
            description:
              translate("UI.INVOICE_PDF_GENERATED") ||
              "Fatura PDF ?nizlemesi a?ildi",
            id: Date.now(),
            title: translate("UI.SUCCESS") || "Success",
            appearance: "success",
          },
        ]);
      } else {
        setToasts([
          {
            description:
              error.response?.data?.message ||
              error.message ||
              translate("UI.INVOICE_CREATE_ERROR") ||
              "Error creating invoice",
            id: Date.now(),
            title: translate("UI.ERROR") || "Error",
            appearance: "danger",
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransferModal = (sale) => {
    setSelectedSaleForTransfer(sale);
    setTransferModalOpen(true);
  };

  const handleTransferToProduction = async () => {
    if (!selectedSaleForTransfer) return;
    try {
      setTransferring(true);
      const response = await axios.post(
        `${API_URL}/api/production-planning/plans/from-sale`,
        { saleId: selectedSaleForTransfer._id },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      if (response.data.success) {
        setToasts([
          {
            description:
              response.data.message || "Is Emri başarıyla oluşturuldu",
            id: Date.now(),
            title: translate("UI.SUCCESS") || "Başarılı",
            appearance: "success",
          },
        ]);
        setTransferModalOpen(false);
      } else {
        throw new Error(response.data.message || "Failed to transfer");
      }
    } catch (error) {
      console.error("Error transferring to production:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            error.message ||
            "Is Emri Oluşturulurken bir hata oluştu",
          id: Date.now(),
          title: translate("UI.ERROR") || "Başarısız",
          appearance: "danger",
        },
      ]);
    } finally {
      setTransferring(false);
    }
  };

  const removeToast = (index) => {
    setToasts((prevToasts) => {
      const newToasts = [...prevToasts];
      newToasts.splice(index, 1);
      return newToasts;
    });
  };

  const handleStatusUpdate = async () => {
    if (!saleInfo._id) return;

    try {
      setLoading(true);

      // Check if devices exist
      if (!saleInfo.devices || saleInfo.devices.length === 0) {
        throw new Error("No devices in sale");
      }

      // Format the companyId properly for the API call
      let formattedCompanyId = null;
      if (saleInfo.companyId) {
        formattedCompanyId =
          typeof saleInfo.companyId === "object"
            ? saleInfo.companyId._id
            : saleInfo.companyId;
      }

      // First update the sale status
      const saleResponse = await axios.put(
        `${API_URL}/api/device-sales/${saleInfo._id}/status`,
        {
          saleName: saleInfo.saleName || "",
          status: "completed",
          paymentStatus: saleInfo.paymentStatus,
          companyId: formattedCompanyId,
          devices: saleInfo.devices.map((device) => {
            return {
              deviceId: device.deviceId,
              serialNumber: device.serialNumber,
              modelId: device.modelId,
              price: device.price || pricePerDevice,
            };
          }),
          totalAmount: calculateTotalAmount(),
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (saleResponse.data.success) {
        setToasts([
          {
            description: translate("UI.SALE_COMPLETED_SUCCESSFULLY"),
            id: Date.now(),
            title: translate("UI.SUCCESS"),
            appearance: "success",
          },
        ]);

        // Refresh the sales list and close the sidebar
        fetchSales();
        setEditSidebarOpened(false);
      } else {
        throw new Error(saleResponse.data.message || "Failed to update sale");
      }
    } catch (error) {
      console.error("Error updating sale:", error.response?.data || error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            error.message ||
            "Error updating sale",
          id: Date.now(),
          title: translate("UI.ERROR"),
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceSelect = async (device) => {
    const selectedDevice = devices.find((d) => d._id === device._id);
    if (selectedDevice) {
      // Check if the device is already in the sale
      const deviceAlreadyExists =
        saleInfo.devices &&
        saleInfo.devices.some((d) => {
          return d.deviceId === device._id;
        });

      if (!deviceAlreadyExists) {
        try {
          setLoading(true);

          // Format the companyId properly for the API call
          let formattedCompanyId = null;
          if (saleInfo.companyId) {
            formattedCompanyId =
              typeof saleInfo.companyId === "object"
                ? saleInfo.companyId._id
                : saleInfo.companyId;
          }

          // First, update the UI state with the new device
          const newDeviceObj = {
            deviceId: selectedDevice._id,
            serialNumber: selectedDevice.serialNumber,
            modelId: selectedDevice.modelId,
            price: pricePerDevice,
          };

          const updatedDevices = [...(saleInfo.devices || []), newDeviceObj];
          const newTotal = updatedDevices.length * pricePerDevice;

          // Create updated sale data for API
          const updatedSaleData = {
            status: saleInfo.status,
            paymentStatus: saleInfo.paymentStatus,
            companyId: formattedCompanyId,
            devices: updatedDevices.map((device) => {
              return {
                deviceId: device.deviceId,
                serialNumber: device.serialNumber,
                modelId: device.modelId,
                price: device.price || pricePerDevice,
              };
            }),
            totalAmount: newTotal,
          };

          // Make the API call to update the sale with the new device
          const response = await axios.put(
            `${API_URL}/api/device-sales/${saleInfo._id}/status`,
            updatedSaleData,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            },
          );

          if (response.data.success) {
            // Update local state with the new device
            setSaleInfo((prev) => ({
              ...prev,
              devices: updatedDevices,
              totalAmount: newTotal,
            }));

            // If adding a device would put us on a new page, update the current page
            const newTotalDevices = updatedDevices.length;
            const newTotalPages = Math.ceil(newTotalDevices / devicesPerPage);
            if (newTotalPages > totalDevicePages) {
              setCurrentDevicePage(newTotalPages);
            }

            // Refresh the devices list to remove the one that was just added to the sale
            await fetchAvailableDevices();

            // Show success toast
            setToasts([
              {
                description: `${selectedDevice.hardwareId} (${
                  selectedDevice.serialNumber
                }) ${translate("UI.DEVICE_ADDED_SUCCESS")}`,
                id: Date.now(),
                title: translations.SUCCESS,
                appearance: "success",
              },
            ]);
          } else {
            throw new Error(
              response.data.message || "Failed to add device to sale",
            );
          }
        } catch (error) {
          console.error(
            "Error adding device to sale:",
            error.response?.data || error,
          );
          setToasts([
            {
              description:
                error.response?.data?.message ||
                error.message ||
                "Error adding device to sale",
              id: Date.now(),
              title: translate("UI.ERROR"),
              appearance: "danger",
            },
          ]);
        } finally {
          setLoading(false);
        }
      } else {
        // Show warning toast if device already exists
        setToasts([
          {
            description: `${selectedDevice.hardwareId} (${
              selectedDevice.serialNumber
            }) ${translate("UI.DEVICE_ALREADY_IN_SALE")}`,
            id: Date.now(),
            title: translate("UI.WARNING"),
            appearance: "warning",
          },
        ]);
      }
    } else {
      console.error("Device not found:", device._id);
      // Show error toast
      setToasts([
        {
          description: translate("UI.DEVICE_NOT_FOUND"),
          id: Date.now(),
          title: translate("UI.ERROR"),
          appearance: "danger",
        },
      ]);
    }
  };

  const handleDeviceRemove = (device) => {
    setDeviceToRemove(device);
    setRemovalType("iade");
    setRemovalDescription("");
    setReplacementDevice(null);
    setRemoveDeviceModalOpen(true);

    // Fetch available devices for replacement
    fetchAvailableDevicesForReplacement();
  };

  const fetchAvailableDevicesForReplacement = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/devices`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      // Frontend tarafinda filtreleme
      const devicesData = response.data.devices || response.data || [];
      const assemblyDevices = devicesData.filter(
        (device) => device.status === "assembly",
      );

      setAvailableDevices(assemblyDevices);
    } catch (error) {
      console.error("?r?nleri getirme hatasi:", error);
    }
  };

  const handleRemoveDeviceConfirm = async () => {
    try {
      setLoading(true);

      // Build a more detailed description for replacements
      let enhancedDescription = removalDescription;

      if (removalType === "degisim" && replacementDevice) {
        const oldDeviceInfo = `${
          deviceToRemove.deviceId?.hardwareId || "Unknown"
        } (S/N: ${deviceToRemove.serialNumber || "Unknown"})`;
        const newDeviceInfo = `${
          replacementDevice.hardwareId || "Unknown"
        } (S/N: ${replacementDevice.serialNumber || "Unknown"})`;

        // Only replace the description if user hasn't provided one
        if (!removalDescription || removalDescription.trim() === "") {
          enhancedDescription = `?r?n degisimi: ${oldDeviceInfo} ?r?n? ${newDeviceInfo} ile degistirildi.`;
        }
      }

      const payload = {
        deviceId: deviceToRemove.deviceId._id,
        removalType: removalType,
        description: enhancedDescription,
        price: pricePerDevice, // Add the current price per device
        status: "assembly", // Ensure device status is set to assembly
      };

      // If replacement, add replacement device and method
      if (removalType === "degisim" && replacementDevice) {
        payload.replacementDeviceId = replacementDevice._id;
        payload.replacement_info = {
          method: "replace", // Use valid enum value: 'replace', 'return', or 'maintenance'
          old_device: {
            hardwareId: deviceToRemove.deviceId?.hardwareId,
            serialNumber: deviceToRemove.serialNumber,
            modelName:
              modelInfoMap[deviceToRemove.deviceId?._id]?.modelName ||
              "Unknown",
          },
          new_device: {
            hardwareId: replacementDevice.hardwareId,
            serialNumber: replacementDevice.serialNumber,
            modelName:
              modelInfoMap[replacementDevice._id]?.modelName || "Unknown",
          },
        };
      }

      const response = await axios.post(
        `${API_URL}/api/device-sales/${saleInfo._id}/remove-device`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: response.data.message,
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        setRemoveDeviceModalOpen(false);
        await fetchSales();

        // Store current page before updating
        const currentPage = currentDevicePage;

        // Update sale info
        const updatedSale = await axios.get(
          `${API_URL}/api/device-sales/${saleInfo._id}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          },
        );
        setSaleInfo(updatedSale.data.sale);

        // Recalculate the price per device based on the updated sale
        if (
          updatedSale.data.sale.devices &&
          updatedSale.data.sale.devices.length > 0
        ) {
          const avgPrice =
            updatedSale.data.sale.totalAmount /
            updatedSale.data.sale.devices.length;
          setPricePerDevice(avgPrice);
        } else {
          setPricePerDevice(0);
        }

        // Adjust current page if needed after device removal
        const newTotalPages = Math.ceil(
          (updatedSale.data.sale.devices?.length || 0) / devicesPerPage,
        );
        if (currentPage > newTotalPages) {
          setCurrentDevicePage(Math.max(1, newTotalPages));
        } else {
          setCurrentDevicePage(currentPage);
        }

        // Refresh devices list after a replacement
        if (removalType === "degisim") {
          await fetchAvailableDevices();
        }
      }
    } catch (error) {
      console.error("?r?n ?ikarma hatası:", error);
      const errorMessage =
        error.response?.data?.message || "?r?n ?ikarilirken bir hata oluştu.";

      setToasts([
        {
          description: errorMessage,
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePricePerDeviceChange = (value) => {
    setPricePerDevice(parseFloat(value) || 0);
    // Update all device prices
    setSaleInfo((prev) => ({
      ...prev,
      devices: prev.devices.map((device) => ({
        ...device,
        price: parseFloat(value) || 0,
      })),
    }));
  };

  // Helper functions
  const calculateTotalAmount = () => {
    return saleInfo.devices.reduce(
      (total, device) => total + (device.price || 0),
      0,
    );
  };

  const getPaymentStatusAppearance = (status) => {
    switch (status) {
      case "paid":
        return "success-subtle";
      case "pending":
        return "warning-subtle";
      case "partial":
        return "info-subtle";
      case "refunded":
        return "danger-subtle";
      default:
        return "warning-subtle";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return formatDateTimeTrInAppTz(dateString);
  };

  // Add a helper function to check if all devices have the same price
  const allSamePriceDevices = (devices) => {
    if (!devices || devices.length <= 1) return true;

    const firstPrice = devices[0].price;
    return devices.every((device) => device.price === firstPrice);
  };

  // Move filteredDevices function here and wrap it in useCallback
  const filteredDevices = useCallback(() => {
    if (!saleInfo.devices) return [];

    const searchLower = deviceSearchQuery.toLowerCase();
    return saleInfo.devices.filter((device) => {
      const hardwareId = device.deviceId?.hardwareId?.toLowerCase() || "";
      const serialNumber = device.serialNumber?.toLowerCase() || "";
      const modelName =
        modelInfoMap[device.deviceId?._id]?.modelName?.toLowerCase() || "";
      const modelCode =
        modelInfoMap[device.deviceId?._id]?.modelCode?.toLowerCase() || "";

      return (
        hardwareId.includes(searchLower) ||
        serialNumber.includes(searchLower) ||
        modelName.includes(searchLower) ||
        modelCode.includes(searchLower)
      );
    });
  }, [saleInfo.devices, deviceSearchQuery, modelInfoMap]);

  // Pagination helpers
  const getCurrentDevices = () => {
    const filtered = filteredDevices();
    if (!filtered || filtered.length === 0) return [];

    const indexOfLastDevice = currentDevicePage * devicesPerPage;
    const indexOfFirstDevice = indexOfLastDevice - devicesPerPage;
    return filtered.slice(indexOfFirstDevice, indexOfLastDevice);
  };

  const totalDevicePages = Math.ceil(
    (filteredDevices().length || 0) / devicesPerPage,
  );

  const paginate = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalDevicePages) return;
    setCurrentDevicePage(pageNumber);
  };

  // Update the getDeviceModelInfo helper function
  const getDeviceModelInfo = async (device) => {
    // 1?? If the device already has `modelId` with full details, return it directly.
    if (device.modelId && device.modelId.name) {
      return {
        modelId: device.modelId._id,
        modelName: device.modelId.name,
        modelCode: device.modelId.code,
      };
    }

    try {
      // 2?? If only `deviceId` exists, fetch the full device details.
      if (!device.deviceId || !device.deviceId._id) {
        console.error("Invalid device object:", device);
        return null;
      }

      const deviceResponse = await axios.get(
        `${API_URL}/api/devices/${device.deviceId._id}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );

      const foundDevice = deviceResponse.data;

      // 3?? Ensure we got the model ID
      if (!foundDevice || !foundDevice.device) {
        console.error("Device or model ID not found:", foundDevice);
        return null;
      }

      // 4?? If model details are already inside `foundDevice.modelId`, return them
      if (foundDevice.device.modelId.name) {
        return {
          modelId: foundDevice.device.modelId._id,
          modelName: foundDevice.device.modelId.name,
          modelCode: foundDevice.device.modelId.code,
        };
      }

      // 5?? Otherwise, fetch the model details separately

      const modelResponse = await axios.get(
        `${API_URL}/api/device-models/${foundDevice.device.modelId}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );

      const model = modelResponse.data.model;

      if (!model) {
        console.error("Model not found:", modelResponse.data);
        return null;
      }

      return {
        modelId: model._id,
        modelName: model.name,
        modelCode: model.code,
      };
    } catch (error) {
      console.error("Error fetching device model info:", error);
      return null;
    }
  };
  useEffect(() => {
    const fetchModelInfo = async () => {
      if (!saleInfo.devices) return;

      const modelInfoPromises = saleInfo.devices.map(async (device) => {
        const deviceId = device.deviceId?._id;
        if (!deviceId) return null;
        const info = await getDeviceModelInfo(device);
        return { deviceId, ...info };
      });

      const results = await Promise.all(modelInfoPromises);
      const newModelInfo = {};
      results.forEach((result) => {
        if (result && result.deviceId) {
          newModelInfo[result.deviceId] = result;
        }
      });

      setModelInfoMap(newModelInfo);
    };

    fetchModelInfo();
  }, [saleInfo.devices]);

  const uniqueCompanies = [
    ...new Set(sales.map((s) => s.companyId?.name).filter(Boolean)),
  ];

  const getPaymentFilterLabel = (status) => {
    if (!status) return null;
    return translate(`UI.${status.toUpperCase()}`) || status;
  };

  const filteredSales = sales.filter((sale) => {
    if (filterCompany && sale.companyId?.name !== filterCompany) return false;
    if (filterDeliveryStatus && sale.status !== filterDeliveryStatus)
      return false;
    if (filterPaymentStatus && sale.paymentStatus !== filterPaymentStatus)
      return false;
    return true;
  });

  const activeFiltersCount = [
    filterCompany,
    filterDeliveryStatus,
    filterPaymentStatus,
  ].filter(Boolean).length;

  const clearSalesFilters = useCallback(() => {
    setFilterCompany(null);
    setFilterDeliveryStatus(null);
    setFilterPaymentStatus(null);
  }, []);

  const salesFilterSections = useMemo(
    () => [
      buildAdaptiveSingleFilterSection({
        id: "company",
        title: translate("UI.COMPANY") || "Firma",
        value: filterCompany || "",
        onChange: (v) => setFilterCompany(v ? String(v) : null),
        options: [
          {
            value: "",
            label: translate("UI.ALL_COMPANIES") || "T?m Cariler",
          },
          ...uniqueCompanies.map((c) => ({
            value: c,
            label: c,
            keywords: [c],
          })),
        ],
        allValue: "",
        allLabel: translate("UI.ALL_COMPANIES") || "T?m Cariler",
        searchPlaceholder: "Firma ara…",
      }),
      buildRadioFilterSection({
        id: "deliveryStatus",
        title: translate("UI.DELIVERY_STATUS") || "Teslimat Durumu",
        value: filterDeliveryStatus || "",
        onChange: (v) => setFilterDeliveryStatus(v ? String(v) : null),
        options: [
          {
            value: "",
            label:
              translate("UI.ALL_DELIVERY_STATUSES") || "T?m Teslimat Durumlari",
          },
          ...["pending", "partial_delivery", "completed", "cancelled"].map(
            (status) => ({
              value: status,
              label: getDeliveryStatusLabel(status),
            }),
          ),
        ],
      }),
      buildRadioFilterSection({
        id: "paymentStatus",
        title: translate("UI.PAYMENT_STATUS") || "?deme Durumu",
        value: filterPaymentStatus || "",
        onChange: (v) => setFilterPaymentStatus(v ? String(v) : null),
        options: [
          {
            value: "",
            label:
              translate("UI.ALL_PAYMENT_STATUSES") || "T?m ?deme Durumlari",
          },
          ...["pending", "partial", "paid"].map((status) => ({
            value: status,
            label: getPaymentFilterLabel(status),
          })),
        ],
      }),
    ],
    [filterCompany, filterDeliveryStatus, filterPaymentStatus, uniqueCompanies],
  );

  return (
    <AppPage breadcrumbs={breadcrumbs}>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">
                {translate("UI.PRODUCT_SALES") || translate("UI.DEVICE_SALES")}
              </h2>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {canWrite ? (
                <Button
                  appearance="primary-animated"
                  onClick={() => navigate("/device-sales/create")}
                >
                  {translate("UI.ADD_PRODUCT_SALE") ||
                    translate("UI.ADD_DEVICE_SALE")}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <Table
                columns={headers}
                visibleColumns={visibleColumns}
                tableId="product-sales-list"
                checkboxSelection
                loading={loading}
                viewOptions={
                  <ListViewOptionsPanel
                    columns={headers}
                    visibleColumns={visibleColumns}
                    onToggleColumn={toggleColumnVisibility}
                    onResetColumns={resetToDefault}
                    filterSections={salesFilterSections}
                    activeFilterCount={activeFiltersCount}
                    onReset={clearSalesFilters}
                    showApply={false}
                  />
                }
              >
                {filteredSales.map((sale, index) => (
                  <Tr key={sale._id || index} id={sale._id} data={sale}>
                    {visibleColumns.includes(0) && (
                      <Td>
                        <div className="d-flex align-items-center gap-2">
                          <FiShoppingCart className="text-primary" />
                          <strong>{sale.saleName || "-"}</strong>
                          {getApprovalStatusLabel(sale.approvalStatus) &&
                            sale.approvalStatus !== "not_required" && (
                              <Lozenge
                                appearance={getApprovalStatusAppearance(
                                  sale.approvalStatus,
                                )}
                              >
                                {getApprovalStatusLabel(sale.approvalStatus)}
                              </Lozenge>
                            )}
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(1) && (
                      <Td>
                        {sale.companyId ? (
                          <div>
                            <div title={sale.companyId.name || "-"}>
                              {limitCompanyName(sale.companyId.name)}
                            </div>
                            {sale.companyId.code && (
                              <small className="text-muted">
                                {sale.companyId.code}
                              </small>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">
                            {translate("UI.NO_COMPANY") || "No Company"}
                          </span>
                        )}
                      </Td>
                    )}
                    {visibleColumns.includes(2) && (
                      <Td>
                        <strong>
                          <ProductPrice
                            value={sale.totalAmount || 0}
                            rate={sale.exchangeRate}
                            showFallbackMark
                          />
                        </strong>
                      </Td>
                    )}
                    {visibleColumns.includes(9) && (
                      <Td>
                        {parseRateNum(sale.exchangeRate) ? (
                          <span
                            className="fw-semibold text-dark"
                            title="Satış anında kaydedilen kur"
                          >
                            {parseRateNum(sale.exchangeRate).toLocaleString(
                              "tr-TR",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              },
                            )}{" "}
                            ₺
                          </span>
                        ) : (
                          <span
                            className="text-muted"
                            title="Kendi kuru bulunamadığı için güncel kur gösterilmektedir"
                          >
                            {parseRateNum(fx?.rate)
                              ? `${parseRateNum(fx.rate).toLocaleString(
                                  "tr-TR",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4,
                                  },
                                )} ₺`
                              : "-"}
                            <span
                              className="text-warning fw-bold ms-1"
                              title="Güncel kur ile hesaplandı (Satışa ait kur kaydı bulunamadı)"
                              style={{ cursor: "help", fontSize: "1.05em" }}
                            >
                              *
                            </span>
                          </span>
                        )}
                      </Td>
                    )}
                    {visibleColumns.includes(3) && (
                      <Td>
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedSaleForModelDetails(sale);
                            setModelDetailsModalOpen(true);
                          }}
                          title={
                            translate("UI.CLICK_TO_VIEW_DETAILS") ||
                            "Click to view model details"
                          }
                        >
                          <FiPackage className="text-info" />
                          <Lozenge appearance="info-subtle">
                            {sale.totalDevices || 0}
                          </Lozenge>
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(4) && (
                      <Td>
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedSaleForModelDetails(sale);
                            setModelDetailsModalOpen(true);
                          }}
                          title={
                            translate("UI.CLICK_TO_VIEW_DETAILS") ||
                            "Click to view model details"
                          }
                        >
                          <FiPackage className="text-success" />
                          <Lozenge appearance="success-subtle">
                            {sale.models?.reduce(
                              (sum, m) => sum + (m.deliveredCount || 0),
                              0,
                            ) || 0}
                          </Lozenge>
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(5) && (
                      <Td>
                        <div className="d-flex align-items-start gap-2">
                          <div className="d-flex flex-column">
                            <span>
                              {sale.saleDate
                                ? formatInstantDateShortTr(sale.saleDate)
                                : "-"}
                            </span>
                            {sale.saleTime && (
                              <small className="text-muted">
                                {sale.saleTime}
                              </small>
                            )}
                          </div>
                        </div>
                      </Td>
                    )}
                    {visibleColumns.includes(6) && (
                      <Td>
                        <Lozenge
                          appearance={getDeliveryStatusAppearance(sale.status)}
                        >
                          {getDeliveryStatusLabel(sale.status)}
                        </Lozenge>
                      </Td>
                    )}
                    {visibleColumns.includes(7) && (
                      <Td>
                        <Lozenge
                          appearance={getSaleStatusAppearance(
                            sale.paymentStatus,
                          )}
                        >
                          {translate(
                            `UI.${sale.paymentStatus?.toUpperCase()}`,
                          ) || sale.paymentStatus}
                        </Lozenge>
                      </Td>
                    )}
                    {visibleColumns.includes(8) && (
                      <Td>
                        <div className="d-flex align-items-center gap-2">
                          <FiEdit2
                            className="cursor-pointer text-grey"
                            style={{
                              fontSize: "20px",
                              cursor:
                                sale?.approvalStatus === "pending"
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                sale?.approvalStatus === "pending" ? 0.4 : 1,
                            }}
                            onClick={() => handleEditSale(sale)}
                            title={
                              sale?.approvalStatus === "pending"
                                ? "Onay bekleyen siparis d?zenlenemez"
                                : translate("UI.EDIT") || "Edit"
                            }
                          />
                          <FiTruck
                            className="text-grey"
                            style={{
                              fontSize: "20px",
                              cursor:
                                sale?.deliveryApproval?.allowed === false
                                  ? "not-allowed"
                                  : "pointer",
                              opacity:
                                sale?.deliveryApproval?.allowed === false
                                  ? 0.4
                                  : 1,
                            }}
                            onClick={() => handleCreateDelivery(sale)}
                            title={
                              sale?.deliveryApproval?.allowed === false
                                ? sale.deliveryApproval.reason ||
                                  "Sevkiyat onayi bekleniyor"
                                : translate("UI.CREATE_DELIVERY") ||
                                  "Create Delivery"
                            }
                          />
                          <FiFileText
                            className="cursor-pointer text-grey"
                            style={{
                              fontSize: "20px",
                              cursor: isSaleApprovalPending(sale)
                                ? "not-allowed"
                                : "pointer",
                              opacity: isSaleApprovalPending(sale) ? 0.4 : 1,
                            }}
                            onClick={() => handleCreateInvoice(sale)}
                            title={
                              isSaleApprovalPending(sale)
                                ? "Onay tamamlanmadan fatura olusturulamaz"
                                : translate("UI.CREATE_INVOICE") ||
                                  "Create Invoice"
                            }
                          />
                          <FiTool
                            className="cursor-pointer text-grey"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={() => handleOpenTransferModal(sale)}
                            title={
                              translate("UI.TRANSFER_TO_PRODUCTION") ||
                              "Is Emri Olustur"
                            }
                          />
                          <FiTrash2
                            className="cursor-pointer text-grey"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={() => handleDeleteSale(sale)}
                            title={translate("UI.DELETE") || "Delete"}
                          />
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Table>
              {filteredSales.some((s) => !parseRateNum(s.exchangeRate)) && (
                <div className="text-muted small mt-2 d-flex align-items-center gap-1">
                  <span className="text-warning fw-bold">*</span>
                  <span>
                    {translate("UI.FALLBACK_EXCHANGE_RATE_NOTE") ||
                      "İşaretli tutarlar ve kurlar, siparişe ait kur kaydı bulunmadığı için güncel kur ile hesaplanmıştır."}
                  </span>
                </div>
              )}
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts.map((toast, index) => (
          <Toast
            key={toast.id}
            {...toast}
            onRemove={() => removeToast(index)}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
            autoDismiss
          />
        ))}
      </ToastContainer>
      <SidebarContainer
        show={detailSidebarOpened}
        title={translate("UI.SALE_DETAILS")}
        onClose={() => {
          setDetailSidebarOpened(false);
          // Reset pagination when closing the sidebar
          setCurrentDevicePage(1);
        }}
      >
        <div className="device-header">
          <div className="me-3">
            <div className="avatar avatar-md bg-primary-subtle rounded">
              <FiShoppingCart
                className="text-primary"
                style={{ width: "24px", height: "24px" }}
              />
            </div>
          </div>
          <div>
            <h5 className="card-title mb-1">
              {translate("UI.SALE_INFORMATION")}
            </h5>
            <p className="text-muted small mb-0">
              {translate("UI.SALE_INFO_DESC")}
            </p>
          </div>
        </div>
        {saleInfo.saleName && (
          <div className="mb-4">
            <Label>{translate("UI.SALE_NAME") || "Sale Name"}</Label>
            <div className="mt-2">
              <strong>{saleInfo.saleName}</strong>
            </div>
          </div>
        )}

        {saleInfo.companyId && (
          <div className="mb-4">
            <Label>{translate("UI.COMPANY")}</Label>
            <div className="mt-2">
              <strong>{saleInfo.companyId?.name}</strong>
              <div className="text-muted small">{saleInfo.companyId?.code}</div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <Label>{translate("UI.SALE_DATE")}</Label>
          <div className="mt-2 gap-1 d-flex align-items-center">
            <FiCalendar className="mr-2 text-muted" />
            {formatDate(saleInfo.saleDate)}
          </div>
        </div>

        <div className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-1">
              <FiPackage className="mr-2 text-muted" />
              <p className="mb-0">{translate("UI.MODELS") || "Models"}</p>
            </div>
            <div className="ms-auto d-flex gap-2">
              <div className="badge bg-primary text-white">
                <span> {translate("UI.TOTAL")}:</span>
                <span>
                  {saleInfo.totalDevices ||
                    saleInfo.models?.reduce(
                      (sum, m) => sum + (m.count || 0),
                      0,
                    ) ||
                    0}
                </span>
              </div>
              {(() => {
                const totalDelivered =
                  saleInfo.models?.reduce(
                    (sum, m) => sum + (m.deliveredCount || 0),
                    0,
                  ) || 0;
                return (
                  <div className="badge bg-success text-white">
                    <span>{translate("UI.DELIVERED") || "Delivered"}:</span>
                    <span>{totalDelivered}</span>
                  </div>
                );
              })()}
            </div>
          </div>

          {saleInfo.models && saleInfo.models.length > 0 && (
            <div className="mt-3">
              {saleInfo.models.map((modelItem, index) => {
                const expectedCount = modelItem.count || 0;
                const deliveredCount = modelItem.deliveredCount || 0;
                const isComplete = deliveredCount >= expectedCount;
                const isPartial =
                  deliveredCount > 0 && deliveredCount < expectedCount;

                return (
                  <div key={index} className="card mb-2 border-0 shadow-sm">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong className="d-block">
                            {modelItem.modelId?.name ||
                              translate("UI.UNKNOWN_MODEL")}
                          </strong>
                          <div className="text-muted small">
                            {modelItem.modelId?.code || ""}
                          </div>
                          {modelItem.modelId?.price && (
                            <div className="text-primary small mt-1">
                              <ProductPrice
                                value={modelItem.modelId.price}
                                rate={saleInfo.exchangeRate}
                                showFallbackMark
                              />{" "}
                              {translate("UI.PER_UNIT") || "per unit"}
                            </div>
                          )}
                        </div>
                        <div className="text-end">
                          <div
                            className={`badge ${
                              isComplete
                                ? "bg-success"
                                : isPartial
                                  ? "bg-warning"
                                  : "bg-info"
                            } text-white`}
                          >
                            {translate("UI.DELIVERED") || "Delivered"}:{" "}
                            {deliveredCount} / {expectedCount}
                          </div>
                          {modelItem.modelId?.price && (
                            <div className="text-muted small mt-1">
                              <ProductPrice
                                value={
                                  (modelItem.modelId.price || 0) * expectedCount
                                }
                                rate={saleInfo.exchangeRate}
                                showFallbackMark
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div className="avatar avatar-sm bg-success-subtle rounded-circle">
                      <FiDollarSign
                        className="text-success"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-muted small">
                      {translate("UI.TOTAL_AMOUNT")}
                    </div>
                    <strong>
                      <ProductPrice
                        value={saleInfo.totalAmount}
                        rate={saleInfo.exchangeRate}
                        showFallbackMark
                      />
                    </strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div className="avatar avatar-sm bg-info-subtle rounded-circle">
                      <FiCreditCard
                        className="text-info"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-muted small">
                      {translate("UI.PAYMENT_METHOD")}
                    </div>
                    <strong>
                      {translate(`UI.${saleInfo.paymentMethod?.toUpperCase()}`)}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div className="avatar avatar-sm bg-warning-subtle rounded-circle">
                      <FiCalendar
                        className="text-warning"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-muted small">
                      {translate("UI.SALE_DATE")}
                    </div>
                    <strong>{formatDate(saleInfo.saleDate)}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div className="avatar avatar-sm bg-success-subtle rounded-circle">
                      <FiCalendar
                        className="text-success"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-muted small">
                      {translate("UI.LAST_DELIVERY_DATE") ||
                        "Last Delivery Date"}
                    </div>
                    <strong>
                      {saleInfo.lastDeliveryDate
                        ? formatDate(saleInfo.lastDeliveryDate)
                        : "-"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-muted small mb-1">
                  {translate("UI.PAYMENT_STATUS")}
                </div>
                <Lozenge
                  appearance={getPaymentStatusAppearance(
                    saleInfo.paymentStatus,
                  )}
                >
                  {translate(`UI.${saleInfo.paymentStatus?.toUpperCase()}`)}
                </Lozenge>
              </div>
              <div className="col-md-6">
                <div className="text-muted small mb-1">
                  {translate("UI.DELIVERY_STATUS")}
                </div>
                <Lozenge
                  appearance={getDeliveryStatusAppearance(saleInfo.status)}
                >
                  {getDeliveryStatusLabel(saleInfo.status)}
                </Lozenge>
              </div>
            </div>
          </div>
        </div>

        {saleInfo.notes && (
          <div className="card mt-4 border-0 shadow-sm">
            <div className="card-body p-3">
              <Label>{translate("UI.NOTES")}</Label>
              <div className="mt-2 p-3 bg-light rounded">{saleInfo.notes}</div>
            </div>
          </div>
        )}
      </SidebarContainer>

      <SidebarContainer
        show={editSidebarOpened}
        title={translate("UI.EDIT_SALE")}
        onClose={() => {
          setEditSidebarOpened(false);
          // Reset pagination when closing the sidebar
          setCurrentDevicePage(1);
        }}
      >
        <div className="d-flex flex-column">
          <div className="device-header">
            <div className="me-3">
              <div className="avatar avatar-md bg-primary-subtle rounded">
                <FiShoppingCart
                  className="text-primary"
                  style={{ width: "24px", height: "24px" }}
                />
              </div>
            </div>
            <div>
              <h5 className="card-title mb-1">
                {translate("UI.SALE_INFORMATION")}
              </h5>
              <p className="text-muted small mb-0">
                {translate("UI.SALE_INFO_DESC")}
              </p>
            </div>
          </div>

          <div className="form-container">
            <div className="form-group">
              <Label>{translate("UI.SALE_NAME") || "Sale Name"}</Label>
              <Input
                type="text"
                value={saleInfo.saleName || ""}
                onChange={(value) =>
                  setSaleInfo((prev) => ({ ...prev, saleName: value }))
                }
                placeholder={
                  translate("UI.ENTER_SALE_NAME") || "Enter sale name"
                }
              />
            </div>
            <div className="form-group">
              <Label>{translate("UI.PAYMENT_STATUS")}</Label>
              <div className="form-control p-0">
                <Dropdown
                  placement="top-start"
                  trigger={
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <Lozenge
                        appearance={getPaymentStatusAppearance(
                          saleInfo.paymentStatus,
                        )}
                      >
                        {translate(
                          `UI.${saleInfo.paymentStatus?.toUpperCase()}`,
                        )}
                      </Lozenge>
                    </div>
                  }
                >
                  <DropdownContent className="w-100">
                    <DropdownItem
                      onClick={() =>
                        setSaleInfo((prev) => ({
                          ...prev,
                          paymentStatus: "pending",
                        }))
                      }
                      active={saleInfo.paymentStatus === "pending"}
                    >
                      <Lozenge appearance="warning-subtle">
                        {translate("UI.PENDING")}
                      </Lozenge>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        setSaleInfo((prev) => ({
                          ...prev,
                          paymentStatus: "paid",
                        }))
                      }
                      active={saleInfo.paymentStatus === "paid"}
                    >
                      <Lozenge appearance="success-subtle">
                        {translate("UI.PAID")}
                      </Lozenge>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        setSaleInfo((prev) => ({
                          ...prev,
                          paymentStatus: "partial",
                        }))
                      }
                      active={saleInfo.paymentStatus === "partial"}
                    >
                      <Lozenge appearance="info-subtle">
                        {translate("UI.PARTIAL")}
                      </Lozenge>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        setSaleInfo((prev) => ({
                          ...prev,
                          paymentStatus: "refunded",
                        }))
                      }
                      active={saleInfo.paymentStatus === "refunded"}
                    >
                      <Lozenge appearance="danger-subtle">
                        {translate("UI.REFUNDED")}
                      </Lozenge>
                    </DropdownItem>
                  </DropdownContent>
                </Dropdown>
              </div>
            </div>

            <div className="form-group">
              <div className="d-flex gap-1 align-items-center mb-3">
                <FiServer className="me-2 text-muted" />
                <label className="mb-0 d-flex align-items-center">
                  {translate("UI.SOLD_DEVICES")}
                </label>
              </div>

              <div className="mb-4">
                <Label>{translate("UI.PRICE_PER_DEVICE")}</Label>
                <Input
                  type="number"
                  value={pricePerDevice}
                  onChange={(value) => handlePricePerDeviceChange(value)}
                  prefix={<FiDollarSign className="text-muted" />}
                />
              </div>

              <div className="mb-3">
                <Label>{translate("UI.ADD_DEVICE")}</Label>
                <div className="form-control p-0">
                  <Dropdown
                    placement="top-start"
                    closeOnSelect={false}
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100">
                        <span>{translate("UI.SELECT_DEVICES")}</span>
                      </div>
                    }
                  >
                    <DropdownContent
                      className="w-100"
                      style={{ maxHeight: "250px", overflowY: "auto" }}
                    >
                      {devices.length > 0 ? (
                        devices
                          .filter((device) => {
                            const isAlreadyInSale = saleInfo.devices?.some(
                              (d) => d.deviceId === device._id,
                            );
                            return (
                              !isAlreadyInSale && device.status === "assembly"
                            );
                          })
                          .map((device) => (
                            <DropdownItem
                              key={device._id}
                              onClick={() => handleDeviceSelect(device)}
                            >
                              <div className="d-flex flex-column">
                                <div>
                                  <strong>{device.hardwareId}</strong> -
                                  {device.modelId?.name ||
                                    translate("UI.LOADING")}
                                </div>
                                <div className="text-muted small">
                                  S/N: {device.serialNumber}
                                </div>
                              </div>
                            </DropdownItem>
                          ))
                      ) : (
                        <DropdownItem disabled>
                          {translate("UI.NO_AVAILABLE_DEVICES")}
                        </DropdownItem>
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>

              <div className="mt-2">
                {saleInfo.devices?.length > 0 ? (
                  <>
                    <div className="mb-2 d-flex justify-content-between align-items-center">
                      <span className="text-muted small">
                        {translate("UI.SELECTED_DEVICES")}:{" "}
                        {saleInfo.devices.length}
                      </span>
                      <span className="text-muted small">
                        {translate("UI.TOTAL")}:{" "}
                        <ProductPrice value={calculateTotalAmount()} />
                      </span>
                    </div>

                    {/* Add search input for edit modal */}
                    <div className="mb-3">
                      <Input
                        type="text"
                        placeholder={translate("UI.SEARCH_DEVICES")}
                        value={deviceSearchQuery}
                        onChange={(value) => {
                          setDeviceSearchQuery(value);
                          setCurrentDevicePage(1); // Reset to first page when searching
                        }}
                        prefix={<FiSearch className="text-muted" />}
                        className="mb-3"
                      />
                    </div>

                    {getCurrentDevices().map((device, index) => {
                      return (
                        <div
                          key={index}
                          className="card mb-3 border-0 shadow-sm hover-shadow"
                        >
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div className="avatar avatar-sm bg-light rounded-circle">
                                    <FiServer
                                      className="text-primary"
                                      style={{ width: "16px", height: "16px" }}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <strong className="d-block mb-1">
                                    {device.deviceId?.hardwareId}
                                  </strong>
                                  <div className="text-primary small">
                                    {
                                      modelInfoMap[device.deviceId?._id]
                                        ?.modelName
                                    }
                                  </div>
                                  <div className="text-muted small">
                                    S/N: {device.serialNumber}
                                  </div>
                                  <div className="mt-2 d-flex gap-2 flex-wrap">
                                    <span className="badge bg-light text-dark border">
                                      {
                                        modelInfoMap[device.deviceId?._id]
                                          ?.modelCode
                                      }
                                    </span>
                                    {!allSamePriceDevices(saleInfo.devices) && (
                                      <span className="badge bg-light text-primary border border-primary">
                                        <ProductPrice value={device.price} />
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <IconButton
                                  icon={
                                    <FiTrash2 style={{ fontSize: "18px" }} />
                                  }
                                  appearance="subtle"
                                  shape="circle"
                                  onClick={() => handleDeviceRemove(device)}
                                  title={translate("UI.REMOVE_DEVICE")}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Improved Pagination for edit sidebar */}
                    {totalDevicePages > 1 && (
                      <div className="pagination-container">
                        <div className="pagination-info">
                          {translate("UI.SHOWING")}{" "}
                          {Math.min(
                            (currentDevicePage - 1) * devicesPerPage + 1,
                            filteredDevices().length,
                          )}
                          -
                          {Math.min(
                            currentDevicePage * devicesPerPage,
                            filteredDevices().length,
                          )}{" "}
                          - {filteredDevices().length} {translate("UI.DEVICE")}
                        </div>
                        <nav aria-label="Device pagination">
                          <ul className="pagination">
                            <li
                              className={`page-item ${
                                currentDevicePage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(1)}
                                aria-label="First"
                                title={translate("UI.FIRST_PAGE")}
                              >
                                <span aria-hidden="true">?</span>
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentDevicePage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(currentDevicePage - 1)}
                                aria-label="Previous"
                                title={translate("UI.PREVIOUS_PAGE")}
                              >
                                <span aria-hidden="true">?</span>
                              </button>
                            </li>

                            {[...Array(totalDevicePages)].map((_, index) => {
                              const pageNumber = index + 1;
                              const isCurrentPage =
                                currentDevicePage === pageNumber;
                              const isNearCurrentPage =
                                Math.abs(currentDevicePage - pageNumber) <= 1 ||
                                pageNumber === 1 ||
                                pageNumber === totalDevicePages;

                              if (
                                !isNearCurrentPage &&
                                pageNumber !== 1 &&
                                pageNumber !== totalDevicePages
                              ) {
                                if (
                                  pageNumber === currentDevicePage - 2 ||
                                  pageNumber === currentDevicePage + 2
                                ) {
                                  return (
                                    <li
                                      key={pageNumber}
                                      className="page-item disabled"
                                    >
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }
                                return null;
                              }

                              return (
                                <li
                                  key={pageNumber}
                                  className={`page-item ${
                                    isCurrentPage ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => paginate(pageNumber)}
                                    aria-label={`Page ${pageNumber}`}
                                    aria-current={
                                      isCurrentPage ? "page" : undefined
                                    }
                                  >
                                    {pageNumber}
                                  </button>
                                </li>
                              );
                            })}

                            <li
                              className={`page-item ${
                                currentDevicePage === totalDevicePages
                                  ? "disabled"
                                  : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(currentDevicePage + 1)}
                                aria-label="Next"
                                title={translate("UI.NEXT_PAGE")}
                              >
                                <span aria-hidden="true">?</span>
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentDevicePage === totalDevicePages
                                  ? "disabled"
                                  : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => paginate(totalDevicePages)}
                                aria-label="Last"
                                title={translate("UI.LAST_PAGE")}
                              >
                                <span aria-hidden="true">?</span>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="alert alert-light text-center">
                    <div className="mb-2">
                      {translate("UI.NO_DEVICES_SELECTED")}
                    </div>
                    <small className="text-muted">
                      {translate("UI.ADD_DEVICES_INSTRUCTION")}
                    </small>
                  </div>
                )}
              </div>

              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{translate("UI.TOTAL_AMOUNT")}</h5>
                  <h5 className="mb-0">
                    <ProductPrice value={calculateTotalAmount()} />
                  </h5>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button
              appearance="primary-animated"
              onClick={handleStatusUpdate}
              loading={loading}
              className="d-flex align-items-center gap-2"
            >
              <FiSave size={16} />
              {translate("UI.SAVE")}
            </Button>
          </div>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
            autoDismiss
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={removeDeviceModalOpen}
        onClose={() => setRemoveDeviceModalOpen(false)}
      >
        <ModalHeader>{translate("UI.REMOVE_DEVICE")}</ModalHeader>
        <ModalBody>
          {removalType === "degisim" && (
            <div className="alert alert-info mb-3">
              <small>
                <strong>{translate("UI.REPLACEMENT_PROCESS_TITLE")}</strong>
                <p className="mb-0 mt-1">
                  {translate("UI.REPLACEMENT_PROCESS_DESCRIPTION")}
                </p>
              </small>
            </div>
          )}

          {deviceToRemove && (
            <div className="mb-4">
              <Label>{translate("UI.DEVICE_INFO")}</Label>
              <div className="card mb-2 border-0 shadow-sm">
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div className="avatar avatar-sm bg-light rounded-circle">
                          <FiServer
                            className="text-primary"
                            style={{ width: "16px", height: "16px" }}
                          />
                        </div>
                      </div>
                      <div>
                        <strong className="d-block mb-1">
                          {deviceToRemove.deviceId?.hardwareId}
                        </strong>
                        <div className="text-primary small">
                          {modelInfoMap[deviceToRemove.deviceId?._id]
                            ?.modelName || "Loading..."}
                        </div>
                        <div className="text-muted small">
                          S/N: {deviceToRemove.serialNumber}
                        </div>
                        <div className="mt-2 d-flex gap-2 flex-wrap">
                          <span className="badge bg-light text-dark border">
                            {modelInfoMap[deviceToRemove.deviceId?._id]
                              ?.modelCode || "Loading..."}
                          </span>
                          <span className="badge bg-light text-primary border border-primary">
                            <ProductPrice value={pricePerDevice} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-group mb-4">
            <Label>{translate("UI.REMOVAL_TYPE")}</Label>
            <div className="form-control p-0">
              <Dropdown
                placement="top-start"
                trigger={
                  <div className="d-flex align-items-center justify-content-between p-2 w-100">
                    <Lozenge
                      appearance={
                        removalType === "iade"
                          ? "info-subtle"
                          : removalType === "bakim"
                            ? "warning-subtle"
                            : "success-subtle"
                      }
                    >
                      {translate(`UI.${removalType?.toUpperCase()}`)}
                    </Lozenge>
                  </div>
                }
              >
                <DropdownContent className="w-100">
                  <DropdownItem
                    onClick={() => setRemovalType("iade")}
                    active={removalType === "iade"}
                  >
                    <Lozenge appearance="info-subtle">
                      {translate("UI.IADE")}
                    </Lozenge>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => setRemovalType("bakim")}
                    active={removalType === "bakim"}
                  >
                    <Lozenge appearance="warning-subtle">
                      {translate("UI.BAKIM")}
                    </Lozenge>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => setRemovalType("degisim")}
                    active={removalType === "degisim"}
                  >
                    <Lozenge appearance="success-subtle">
                      {translate("UI.DEGISIM")}
                    </Lozenge>
                  </DropdownItem>
                </DropdownContent>
              </Dropdown>
            </div>
          </div>

          {removalType === "degisim" && (
            <div className="form-group mb-4">
              <Label>{translate("UI.REPLACEMENT_DEVICE")}</Label>
              <div className="alert alert-info mb-2">
                <small>{translate("UI.REPLACEMENT_DEVICE_INFO")}</small>
              </div>
              <div className="form-control p-0">
                <Dropdown
                  placement="top-start"
                  trigger={
                    <div className="d-flex align-items-center justify-content-between p-2 w-100">
                      <span>
                        {replacementDevice
                          ? `${replacementDevice.hardwareId} - ${replacementDevice.serialNumber}`
                          : translate("UI.SELECT_DEVICE")}
                      </span>
                    </div>
                  }
                >
                  <DropdownContent className="w-100">
                    {availableDevices.length > 0 ? (
                      availableDevices.map((device) => (
                        <DropdownItem
                          key={device._id}
                          onClick={() => setReplacementDevice(device)}
                        >
                          <div>
                            <div>
                              <strong>{device.hardwareId}</strong> -{" "}
                              {modelInfoMap[device._id]?.modelName}
                            </div>
                            <div className="text-muted small">
                              S/N: {device.serialNumber}
                            </div>
                          </div>
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem disabled>
                        {translate("UI.NO_AVAILABLE_DEVICES")}
                      </DropdownItem>
                    )}
                  </DropdownContent>
                </Dropdown>
              </div>
              {replacementDevice && (
                <div className="mt-2 p-2 bg-light rounded border">
                  <div className="row">
                    <div className="col-6 border-end">
                      <div className="text-muted small mb-1">
                        {translate("UI.CURRENT_DEVICE")}:
                      </div>
                      <div className="fw-semibold">
                        {deviceToRemove?.deviceId?.hardwareId}
                      </div>
                      <div className="small">
                        S/N: {deviceToRemove?.serialNumber}
                      </div>
                      <div className="small">
                        {modelInfoMap[deviceToRemove.deviceId?._id]?.modelName}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted small mb-1">
                        {translate("UI.NEW_DEVICE")}:
                      </div>
                      <div className="fw-semibold">
                        {replacementDevice.hardwareId}
                      </div>
                      <div className="small">
                        S/N: {replacementDevice.serialNumber}
                      </div>
                      <div className="small">
                        {modelInfoMap[replacementDevice._id]?.modelName}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <Label>{translate("UI.REMOVAL_DESCRIPTION")}</Label>
            <Input
              type="textarea"
              value={removalDescription}
              onChange={(value) => setRemovalDescription(value)}
              placeholder={translate("UI.ENTER_REMOVAL_DESCRIPTION")}
            />
          </div>

          <div className="my-3">
            <ButtonGroup placement="end">
              <Button
                appearance="subtle"
                onClick={() => setRemoveDeviceModalOpen(false)}
              >
                {translate("UI.CANCEL")}
              </Button>
              <Button
                appearance="primary-animated"
                onClick={handleRemoveDeviceConfirm}
                loading={loading}
                disabled={
                  (removalType === "degisim" && !replacementDevice) ||
                  !removalDescription
                }
              >
                {translate("UI.CONFIRM")}
              </Button>
            </ButtonGroup>
          </div>
        </ModalBody>
      </Modal>

      {/* Model Details Modal */}
      <Modal
        isOpen={modelDetailsModalOpen}
        onClose={() => setModelDetailsModalOpen(false)}
      >
        <ModalBody>
          <div
            className="device-header"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <div>
                <h4 className="card-title mb-1">
                  {translate("UI.SALES_DELIVERY_DETAILS") ||
                    "Siparis Teslim Detaylari"}
                </h4>
                <p className="text-muted small mb-0">
                  {selectedSaleForModelDetails?.saleName
                    ? selectedSaleForModelDetails.saleName
                    : ""}
                </p>
              </div>
            </div>
            <IconButton
              icon={<FiX style={{ fontSize: "18px" }} />}
              appearance="subtle"
              shape="circle"
              onClick={() => setModelDetailsModalOpen(false)}
              title={translate("UI.CLOSE") || "Kapat"}
            />
          </div>
          {selectedSaleForModelDetails?.models &&
          selectedSaleForModelDetails.models.length > 0 ? (
            <div>
              {(() => {
                const totalDevices =
                  selectedSaleForModelDetails.totalDevices || 0;
                const totalDelivered =
                  selectedSaleForModelDetails.models?.reduce(
                    (sum, m) => sum + (m.deliveredCount || 0),
                    0,
                  ) || 0;
                const totalModels = selectedSaleForModelDetails.models.length;

                return (
                  <>
                    <div className="models-summary-simple mb-3">
                      <div className="summary-item">
                        <span className="summary-label">
                          {translate("UI.TOTAL_MODELS") || "Total Models"}:
                        </span>
                        <span className="summary-value">{totalModels}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">
                          {translate("UI.TOTAL_DEVICES") || "Total Devices"}:
                        </span>
                        <span className="summary-value">{totalDevices}</span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">
                          {translate("UI.TOTAL_DELIVERED") || "Total Delivered"}
                          :
                        </span>
                        <span className="summary-value text-success">
                          {totalDelivered}
                        </span>
                      </div>
                    </div>
                    {selectedSaleForModelDetails.models.map(
                      (modelItem, index) => {
                        const expectedCount = modelItem.count || 0;
                        const deliveredCount = modelItem.deliveredCount || 0;

                        return (
                          <div key={index} className="model-item-simple">
                            <div className="model-item-header">
                              <div className="model-name-simple">
                                {modelItem.modelId?.name ||
                                  translate("UI.UNKNOWN_MODEL") ||
                                  "Unknown Model"}
                                {modelItem.modelId?.code && (
                                  <span className="text-muted small ms-2">
                                    ({modelItem.modelId.code})
                                  </span>
                                )}
                              </div>
                              <div className="d-flex">
                                <Lozenge appearance="success-subtle">
                                  {" "}
                                  {deliveredCount}
                                </Lozenge>
                                <span className="text-muted small mx-1">/</span>
                                <Lozenge appearance="info-subtle">
                                  {" "}
                                  {expectedCount}
                                </Lozenge>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="empty-models-state">
              <FiPackage size={32} className="empty-icon" />
              <div className="empty-text">
                {translate("UI.NO_MODELS_FOUND") || "No models found"}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        size="md"
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <FiTool className="text-primary" />
            <h5 className="mb-0">
              {translate("UI.TRANSFER_TO_PRODUCTION") || "Is Emri Olustur"}
            </h5>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="py-2">
            <p
              className="fs-5 mb-0"
              style={{ lineHeight: "1.5", color: "#374151" }}
            >
              <strong>
                {selectedSaleForTransfer?.saleNo ||
                  selectedSaleForTransfer?.notes ||
                  "Bu siparis"}
              </strong>{" "}
              i?erigindeki modelleri is emri olusturmak istediginize emin
              misiniz?
            </p>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              appearance="subtle"
              onClick={() => setTransferModalOpen(false)}
            >
              {translate("UI.CANCEL") || "Iptal"}
            </Button>
            <Button
              appearance="primary-animated"
              onClick={handleTransferToProduction}
              loading={transferring}
              disabled={transferring}
            >
              {transferring ? "Olusturuluyor..." : "Onayla ve Olustur"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PdfPreviewModal
        isOpen={pdfPreview.open}
        onClose={closePdfPreview}
        title={pdfPreview.title}
        previewUrl={pdfPreview.url}
        fileName={pdfPreview.fileName}
        loading={pdfPreview.loading}
        error={pdfPreview.error}
        onDownload={downloadPdfFromPreview}
      />
    </AppPage>
  );
};

export default withAuth(DeviceSales, ["admin", "employee", "user"]);
